import cn from 'classnames';
import { FC, useCallback, useRef } from 'react';

import { FREE_ROTATION_MAX, FREE_ROTATION_MIN } from '../../config';

import styles from './RotationSlider.module.scss';

type RotationSliderProps = {
  className: string;
  value: number;
  onChange: (angle: number) => void;
};

const PIXELS_PER_DEGREE = 4;
const TOTAL_RANGE = FREE_ROTATION_MAX - FREE_ROTATION_MIN;
const TRACK_WIDTH = TOTAL_RANGE * PIXELS_PER_DEGREE;

// Marks every 2°: dots between major ticks
const MARK_STEP = 2;
// Major ticks (vertical lines) every 10°
const MAJOR_STEP = 10;

const marks = Array.from(
  { length: TOTAL_RANGE / MARK_STEP + 1 },
  (_, i) => FREE_ROTATION_MIN + i * MARK_STEP,
);

export const RotationSlider: FC<RotationSliderProps> = (props) => {
  const { className, value, onChange } = props;

  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartValue = useRef(0);

  const clampAngle = useCallback(
    (angle: number) =>
      Math.round(
        Math.max(FREE_ROTATION_MIN, Math.min(FREE_ROTATION_MAX, angle)) * 10,
      ) / 10,
    [],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      isDragging.current = true;
      dragStartX.current = event.clientX;
      dragStartValue.current = value;
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    },
    [value],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!isDragging.current) {
        return;
      }

      const deltaX = event.clientX - dragStartX.current;
      const deltaAngle = -deltaX / PIXELS_PER_DEGREE;

      onChange(clampAngle(dragStartValue.current + deltaAngle));
    },
    [onChange, clampAngle],
  );

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Track offset: shift so current value aligns with the fixed center indicator
  const trackOffset = -value * PIXELS_PER_DEGREE - TRACK_WIDTH / 2;

  return (
    <div
      className={cn(styles.container, className)}
      data-component="photo-rotation-slider"
    >
      <div className={styles.bubble}>
        <span className={styles.bubbleText}>{value.toFixed(1)}&deg;</span>
      </div>

      <div className={styles.indicator} />

      <div
        className={styles.slider}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div
          className={styles.track}
          style={{
            width: TRACK_WIDTH,
            transform: `translateX(${trackOffset}px)`,
          }}
        >
          {marks.map((angle) => {
            const isMajor = angle % MAJOR_STEP === 0;
            const isCenter = angle === 0;

            return (
              <div key={angle} className={styles.markWrapper}>
                {isCenter ? (
                  <span className={styles.centerLabel}>0</span>
                ) : isMajor ? (
                  <div className={styles.tick} />
                ) : (
                  <div className={styles.dot} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
